import { useState, useEffect } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Building, Building2, CreditCard, DollarSign, Clock, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const AdminContractorProfile = () => {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      const response = await axios.get(`${API}/company-profile`);
      setProfile(response.data);
    } catch (error) {
      toast.error("Failed to load contractor profile");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-[#F37021]" />
      </div>
    );
  }

  return (
    <div data-testid="admin-contractor-profile">
      <div className="flex items-center gap-3 mb-2">
        <Building className="w-8 h-8 text-[#3B82F6]" />
        <h1 className="text-4xl font-bold text-[#333333]">CONTRACTOR PROFILE</h1>
      </div>
      <p className="text-[#52525B] mb-2">View Gavs Bemanning AB company information and rates</p>
      <Badge variant="outline" className="mb-8 text-gray-500 border-gray-300">Read Only</Badge>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Company Information Card */}
        <Card>
          <CardHeader>
            <CardTitle className="text-xl flex items-center gap-2">
              <Building2 className="w-5 h-5 text-[#F37021]" />
              COMPANY INFORMATION
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Logo Display */}
            {profile?.logo_data_url && (
              <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-lg">
                <div className="w-20 h-20 bg-white rounded-lg border flex items-center justify-center overflow-hidden">
                  <img 
                    src={profile.logo_data_url}
                    alt="Company Logo"
                    className="w-full h-full object-contain"
                  />
                </div>
                <p className="text-sm text-gray-600">Company Logo</p>
              </div>
            )}

            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-xs text-gray-500 uppercase mb-1">Company Name</p>
                <p className="font-medium">{profile?.company_name || '-'}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500 uppercase mb-1">Registration Number</p>
                <p className="font-medium">{profile?.registration_number || '-'}</p>
              </div>
            </div>

            <div>
              <p className="text-xs text-gray-500 uppercase mb-1">Address</p>
              <p className="font-medium">{profile?.address || '-'}</p>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-xs text-gray-500 uppercase mb-1">Email</p>
                <p className="font-medium">{profile?.email || '-'}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500 uppercase mb-1">Phone</p>
                <p className="font-medium">{profile?.phone || '-'}</p>
              </div>
            </div>

            <div>
              <p className="text-xs text-gray-500 uppercase mb-1">VAT Number (Moms)</p>
              <p className="font-medium">{profile?.vat_number || '-'}</p>
            </div>
          </CardContent>
        </Card>

        {/* Rate Card */}
        <Card>
          <CardHeader>
            <CardTitle className="text-xl flex items-center gap-2">
              <CreditCard className="w-5 h-5 text-[#F37021]" />
              RATE CARD
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-sm text-blue-700">
                These rates are set by the contractor and used for quote calculations.
              </p>
            </div>

            <div className="space-y-3">
              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center gap-3">
                  <DollarSign className="w-5 h-5 text-blue-600" />
                  <p className="font-medium">Labour (daily)</p>
                </div>
                <p className="text-lg font-bold text-[#333333]">
                  {(profile?.rate_card?.daily_rate_labour || 0).toLocaleString('sv-SE')} SEK
                </p>
              </div>

              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center gap-3">
                  <DollarSign className="w-5 h-5 text-green-600" />
                  <p className="font-medium">Technical (daily)</p>
                </div>
                <p className="text-lg font-bold text-[#333333]">
                  {(profile?.rate_card?.daily_rate_technical || 0).toLocaleString('sv-SE')} SEK
                </p>
              </div>

              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center gap-3">
                  <DollarSign className="w-5 h-5 text-blue-600" />
                  <p className="font-medium">Labour Holiday (daily)</p>
                </div>
                <p className="text-lg font-bold text-[#333333]">
                  {(profile?.rate_card?.daily_rate_labour_holiday || 0).toLocaleString('sv-SE')} SEK
                </p>
              </div>

              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center gap-3">
                  <DollarSign className="w-5 h-5 text-green-600" />
                  <p className="font-medium">Technical Holiday (daily)</p>
                </div>
                <p className="text-lg font-bold text-[#333333]">
                  {(profile?.rate_card?.daily_rate_technical_holiday || 0).toLocaleString('sv-SE')} SEK
                </p>
              </div>

              <div className="flex items-center justify-between p-3 bg-orange-50 rounded-lg border-t-2 border-orange-200">
                <div className="flex items-center gap-3">
                  <Clock className="w-5 h-5 text-orange-600" />
                  <p className="font-medium">Overtime (hourly)</p>
                </div>
                <p className="text-lg font-bold text-[#333333]">
                  {(profile?.rate_card?.hourly_rate_overtime || 0).toLocaleString('sv-SE')} SEK
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};
